import React, { useEffect, useRef, useState } from "react";
import { useSite } from "../lib/SiteContext";
import api from "../lib/api";
import BridgeTokenCard from "../components/BridgeTokenCard";
import { Settings as Cog, Save } from "lucide-react";

const FIELDS = [
  ["name", "Site name", "text"],
  ["location_name", "Location label", "text"],
  ["latitude", "Latitude", "number"],
  ["longitude", "Longitude", "number"],
  ["roof_area_m2", "Roof area (m²)", "number"],
  ["runoff_factor", "Runoff factor (0–1)", "number", "0.01"],
  ["tank_capacity_liters", "Tank capacity (L)", "number"],
  ["tank_dead_volume_liters", "Tank dead volume (L)", "number"],
  ["liters_per_kwh", "Liters per kWh (pump)", "number"],
  ["daily_water_usage_liters", "Daily water usage (L)", "number"],
  ["timezone", "Timezone (IANA)", "text"],
];

export default function Settings() {
  const { currentSite, refresh } = useSite();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState(null);
  // Track the site we last hydrated the form from. The full `currentSite`
  // reference can change across unrelated context refreshes; we only want to
  // wipe form state when the *site identity* actually changed (e.g. site
  // switcher). The ref guard keeps deps exhaustive without resorting to a
  // suppression directive.
  const hydratedSiteIdRef = useRef(null);

  useEffect(() => {
    if (!currentSite) return;
    if (hydratedSiteIdRef.current === currentSite.id) return;
    hydratedSiteIdRef.current = currentSite.id;
    const next = {};
    FIELDS.forEach(([k]) => { next[k] = currentSite[k] ?? ""; });
    setForm(next);
  }, [currentSite]);

  const update = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setSaved(false); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null); setSaved(false);
    const payload = {};
    FIELDS.forEach(([k, , type]) => {
      const v = form[k];
      if (v === "" || v == null) return;
      payload[k] = type === "number" ? Number(v) : v;
    });
    try {
      await api.patch(`/sites/${currentSite.id}`, payload);
      await refresh();
      setSaved(true);
    } catch (ex) { setErr(ex.userMessage || "Failed to save"); }
    finally { setBusy(false); }
  };

  if (!currentSite) return null;

  return (
    <div className="space-y-8 pb-12 max-w-3xl" data-testid="settings-page">
      <header>
        <div className="label-tiny mb-1.5 flex items-center gap-2"><Cog className="w-3.5 h-3.5" /> Settings</div>
        <h1 className="font-heading text-3xl font-semibold">Site configuration</h1>
        <p className="text-sm text-muted-foreground mt-1">Tune your home's physical parameters. Your KPIs update instantly on save.</p>
      </header>

      <form onSubmit={submit} className="card-base p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          {FIELDS.map(([k, label, type, step]) => (
            <label key={k} className="block">
              <span className="label-tiny block mb-1.5">{label}</span>
              <input
                type={type} step={step}
                value={form[k] ?? ""}
                onChange={(e) => update(k, e.target.value)}
                data-testid={`settings-${k}`}
                className="w-full px-3 py-2 rounded-md bg-zinc-950 border border-border focus:border-zinc-500 outline-none text-sm font-mono"
              />
            </label>
          ))}
        </div>
        <div className="text-xs text-muted-foreground border border-border rounded-md p-3 leading-relaxed">
          <span className="text-foreground font-medium">Liters per kWh:</span> recommended range 450–550 for typical pressure systems. Used to convert pump-socket cumulative kWh into estimated liters.
        </div>
        {err && <div className="text-xs text-[#EF4444] bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-md p-2.5">{err}</div>}
        {saved && <div className="text-xs text-[#10B981] bg-[#10B981]/10 border border-[#10B981]/20 rounded-md p-2.5">Saved · KPIs will refresh on the next page load.</div>}
        <div className="flex justify-end pt-1">
          <button type="submit" disabled={busy} data-testid="settings-save"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-foreground text-background text-sm font-medium disabled:opacity-50">
            <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>

      <BridgeTokenCard siteId={currentSite.id} />

      <section className="card-base p-6">
        <h2 className="font-heading text-lg font-semibold">Connector security</h2>
        <p className="text-sm text-muted-foreground mt-1">API keys for external services (Wunderground, FusionSolar) are stored server-side and never exposed to the browser. Configure each provider on the Data Sources page.</p>
      </section>
    </div>
  );
}
